import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { postApiCallerFormData } from "../../libs/apiCaller";

const AddOffer = ({ isOpen, onClose, getOfferData }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    percentage: Yup.number()
      .required("Percentage is required")
      .min(0, "Percentage must be at least 0")
      .max(99, "maximum 2 digits are acceptable"),
    image: Yup.mixed().required("Image is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      percentage: "",
      image: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      // Create a FormData object
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("percentage", values.percentage);

      // Check if the image exists before appending
      if (values.image instanceof File) {
        formData.append("image", values.image);
      }
      const endPoint = "offer_add";
      const response = await postApiCallerFormData(endPoint, formData);

      if (response?.data?.response_code === 200) {
        getOfferData();
        setLoading(false);
      }
      // else if (response.response.data.response_code === 403) {
      //   redirectToLogin();
      // }
      onClose(); // Close modal after submission
      setLoading(false);
    },
  });

  // Handle Image Upload
  const handleImageUpload = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("image", file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Reset Form when Modal Closes
  useEffect(() => {
    formik.resetForm();
    if (!isOpen) {
      formik.resetForm();
      setPreviewImage(null);
    }
  }, [isOpen]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          onClose();
        }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-lg sm:max-w-md md:max-w-lg">
              <div className="bg-[#21140E0F] px-4 pb-6 pt-3 sm:px-6">
                <div className="flex justify-between items-center">
                  <DialogTitle className="text-lg sm:text-xl font-bold text-[#284184]">
                    Add Offer
                  </DialogTitle>
                  <button
                    onClick={() => {
                      onClose();
                    }}
                    className="text-[#1C2024] hover:bg-gray-100 p-1.5 rounded-full transition-colors"
                  >
                    <span className="text-2xl sm:text-4xl leading-none">
                      &times;
                    </span>
                  </button>
                </div>
                <div>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    {/* Title Field */}
                    <div>
                      <label>Title</label>
                      <input
                        type="text"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full border rounded-md mt-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      {formik.touched.title && formik.errors.title && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.title}
                        </p>
                      )}
                    </div>

                    {/* Description Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Percentage
                      </label>
                      <input
                        type="number"
                        name="percentage"
                        value={formik.values.percentage}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full border rounded-md mt-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      {formik.touched.percentage &&
                        formik.errors.percentage && (
                          <p className="text-red-500 text-xs mt-1">
                            {formik.errors.percentage}
                          </p>
                        )}
                    </div>

                    {/* Image Upload Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Image
                      </label>

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        className="hidden"
                      />

                      {/* Custom Button to Trigger File Input */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                      >
                        Choose Image
                      </button>

                      {/* Validation Error Message */}
                      {formik.touched.image && formik.errors.image && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.image}
                        </p>
                      )}

                      {/* Image Preview */}
                      {previewImage && (
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="mt-2 w-32 h-32 object-cover rounded-md"
                        />
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="text-right">
                      <button
                        type="submit"
                        className="text-white bg-[#284184] rounded-xl border px-4 pt-1 pb-2"
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Submit"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddOffer;
